import { setCookie } from "@insite/client-framework/Common/Cookies";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCart from "@insite/client-framework/Store/Data/Carts/Handlers/RemoveCart";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
// import Icon, { IconProps } from "@insite/mobius/Icon"; // Valtech Custom Code
import Globe from "@insite/mobius/Icons/Globe";
import MapPin from "@insite/mobius/Icons/MapPin";
import Menu from "@insite/mobius/Icons/Menu";
import { MappedLink } from "@insite/mobius/Menu";
import PanelMenu, { PanelMenuPresentationProps } from "@insite/mobius/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

/* Valtech Custom Code */
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import ArrowBackward from "@store/Components/Icons/ArrowBackward";
import ChevronRight from "@store/Components/Icons/ChevronRight";
import Close from "@store/Components/Icons/Close";
import StoreMenu from "@store/Components/Icons/Menu";
import { headerLinkListIcons } from "@store/Overrides/Widgets/Header/HeaderLinkList";
import { StoreLinkModel } from "@store/Store/Links/LinksSelectors";
import { TYPOGRAPHY } from "@store/theme/design";
import { buttonResetHoverStyles } from "@store/theme/mixins";
import { menuMobileButtonProps } from "@store/theme/props/menu";
/* Valtech Custom Code */

const mapStateToProps = (state: ApplicationState) => {
    /* Valtech Custom Code */
    const headerLinkListLinkFields = getWidgetsByPageId(state, getHeader(state).id)
        .filter((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })
        .reduce((y, x) => y.concat(x.fields.links), [] as StoreLinkModel[]);
    /* Valtech Custom Code */

    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        userName: state.context.session?.userName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        vmiDashboardPageUrl: getPageLinkByPageType(state, "VmiDashboardPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        flyoutDrawerIsOpen: state.components.addressDrawer.flyoutDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        cart: getCurrentCartState(state).value,
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    removeCart,
};

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    displayVmiNavigation?: boolean;
    vmiPageLinks?: (PageLinkModel | undefined)[];
    displayModeSwitch?: boolean;
    currentMode?: string;
    isCompactHeaderFlyout?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & {
        typographyProps?: TypographyPresentationProps; // Valtech Custom Code
        iconProps?: IconPresentationProps; // Valtech Custom Code
        textWrapper?: TypographyPresentationProps; // Valtech Custom Code
    };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

/* Valtech Custom Code */
const menuItemBorderWidth = "1px";
const mobileMenuBackgroundColor = "common.background";

const rowCurrentStyles = () => {
    return css<any>`
        ${({ isCurrent }) => {
            /* !important required to override active styles since the injectCss is before the existing styles */
            if (isCurrent) {
                return css`
                    background: ${getColor("common.accent")} !important;
                    color: ${getColor("text")} !important;
                `;
            }
        }}
    `;
};

const rowViewAllStyles = () => {
    return css<any>`
        ${({ hasChildren }) => {
            /* !important required to override active styles since the injectCss is before the existing styles */
            if (!hasChildren) {
                return css`
                    &:first-child {
                        color: ${getColor("primary")} !important;
                    }
                `;
            }
        }}
    `;
};

const panelRowProps = {
    color: mobileMenuBackgroundColor,
    moreIconProps: {
        src: ChevronRight,
        size: 24,
    },
    css: css`
        ${rowCurrentStyles()}
        border-top: ${menuItemBorderWidth} solid ${getColor("common.borderDark")};
        min-height: 50px;

        span {
            color: inherit;
        }

        &:last-child {
            border-bottom: ${menuItemBorderWidth} solid ${getColor("common.borderDark")};
        }
    `,
};
/* Valtech Custom Code */

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: menuMobileButtonProps,
    drawerSectionWrapper: {
        css: css`
            &:first-child {
                margin-top: 0;
                color: transparent;
            }
            &:last-child {
                margin-bottom: 16px;
            }
            margin-top: 16px;
        `,
    },
    panelMenu: {
        cssOverrides: {
            wrapper: css`
                > button {
                    width: calc(100% - 2px);
                }

                &:last-child {
                    border-bottom: ${menuItemBorderWidth} solid ${getColor("common.borderDark")};
                }
            `,
            children: css`
                button {
                    min-width: 0;
                    ${buttonResetHoverStyles()}
                }
            `,
        },
        childTypographyProps: {
            color: "text",
            size: TYPOGRAPHY.size.smaller,
        },
        closeIconProps: {
            src: Close,
            size: 12,
            css: css`
                margin-right: -4px;
            `,
        },
        backIconProps: {
            src: ArrowBackward,
            height: 20,
            width: 20,
        },
        headerColor: mobileMenuBackgroundColor,
        bodyColor: mobileMenuBackgroundColor,
        panelRowProps: {
            ...panelRowProps,
            css: css`
                ${panelRowProps.css}
                ${rowViewAllStyles()}
            `,
        },
    },
    mainNavigationRow: panelRowProps,
    mainNavigationRowTypography: {
        transform: "uppercase",
        size: TYPOGRAPHY.size.smaller,
    },
    logoLinks: {
        color: mobileMenuBackgroundColor,
        css: css`
            ${rowCurrentStyles()}
            padding: 6px 20px;
            line-height: ${TYPOGRAPHY.lineHeight.small};
        `,
        textWrapper: {
            css: css`
                display: flex;
                align-items: center;
            `,
        },
        typographyProps: {
            ellipsis: false,
            transform: "initial",
        },
        iconProps: {
            color: "inherit",
            size: 20,
            css: css`
                margin-right: 0.25em;
            `,
        },
    },
};

const styles = navigationDrawerStyles;

const NavigationDrawer = ({
    links,
    showQuickOrder,
    quickOrderLink,
    displayVmiNavigation,
    vmiPageLinks,
    displayModeSwitch,
    currentMode,
    isCompactHeaderFlyout,
    currencies,
    currentCurrencyId,
    currentCurrencySymbol,
    languages,
    currentLanguage,
    currentLocation,
    userName,
    isGuest,
    myAccountPageLink,
    signInUrl,
    vmiDashboardPageUrl,
    homePageUrl,
    headerLinkListLinkFields,
    showCustomerMenuItem,
    fulfillmentLabel,
    navDrawerIsOpen,
    flyoutDrawerIsOpen,
    isPunchOutSession,
    cart,
    history,
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    removeCart,
}: Props) => {
    const headerLinkListLinks = useGetLinks(headerLinkListLinkFields, o => o.fields.destination);
    const openDrawer = () => {
        isCompactHeaderFlyout ? setFlyoutDrawerIsOpen({ isOpen: true }) : setNavDrawerIsOpen({ navDrawerIsOpen: true });
        setInitialValues({});
    };

    const closeDrawer = () => {
        if (isCompactHeaderFlyout) {
            setFlyoutDrawerIsOpen({ isOpen: false });
            setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
            return;
        }

        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const onSignOutHandler = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const navigationModeChangeHandler = (navigationMode: "Storefront" | "Vmi") => {
        setCookie(NavigationModeCookieName, navigationMode);
        if (navigationMode === "Vmi") {
            vmiDashboardPageUrl && history.push(vmiDashboardPageUrl);
        } else if (cart?.status === "AlternateCart") {
            removeCart({
                cartId: API_URL_CURRENT_FRAGMENT,
                isAlternateCart: true,
                onSuccess: () => {
                    homePageUrl && history.push(homePageUrl);
                },
            });
        } else {
            homePageUrl && history.push(homePageUrl);
        }
    };

    const currentPageUrl = currentLocation.pathname;
    const isOpen = isCompactHeaderFlyout ? flyoutDrawerIsOpen : navDrawerIsOpen;

    return (
        <>
            <Button
                className="menu-trigger-button"
                onClick={openDrawer}
                {...styles.menuTriggerButton}
                data-test-selector="expandMobileMenu"
            >
                <ButtonIcon className="menu-trigger-button-icon" src={StoreMenu /* Valtech Custom Code */} />
                <VisuallyHidden>{translate("menu")}</VisuallyHidden>
            </Button>
            <Drawer
                className="drawer"
                draggable
                position="left"
                {...styles.drawer}
                isOpen={isOpen}
                handleClose={closeDrawer}
                contentLabel="menu drawer"
            >
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {/* Valtech Removed Code - Sign in */}
                    {/* covers MainNavigation functionality */}
                    {!displayVmiNavigation &&
                        links.map((link, index) => {
                            const childrens = link.children?.filter(x => !x.excludeFromNavigation); // Valtech Custom Code
                            if (childrens && childrens.length > 0) {
                                return (
                                    <PanelMenu
                                        className="panel-menu"
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        currentUrl={currentPageUrl}
                                        closeOverlay={closeDrawer}
                                        panelTrigger={
                                            <PanelRow
                                                className="main-navigation-row"
                                                hasChildren
                                                {...styles.mainNavigationRow}
                                                isCurrent={currentPageUrl === link.url /* Valtech Custom Code */}
                                            >
                                                <Typography
                                                    className="main-navigation-row-text"
                                                    {...styles.mainNavigationRowTypography}
                                                >
                                                    {link.title}
                                                </Typography>
                                            </PanelRow>
                                        }
                                        menuItems={link.children}
                                        maxDepth={link.maxDepth || 3}
                                        layer={0}
                                        {...styles.panelMenu}
                                    />
                                );
                            }
                            return (
                                <PanelRow
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    href={link.url}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                    className="main-navigation-row"
                                    onClick={closeDrawer}
                                    {...styles.mainNavigationRow}
                                    isCurrent={currentPageUrl === link.url /* Valtech Custom Code */}
                                >
                                    <Typography
                                        className="main-navigation-row-text"
                                        {...styles.mainNavigationRowTypography}
                                    >
                                        {link.title}
                                    </Typography>
                                </PanelRow>
                            );
                        })}
                    {/* Valtech Removed Code - Quick Order */}
                    {displayVmiNavigation &&
                        vmiPageLinks &&
                        vmiPageLinks.map(
                            link =>
                                link && (
                                    <PanelRow
                                        className="main-navigation-row"
                                        key={link.id}
                                        isCurrent={currentPageUrl === link.url}
                                        onClick={closeDrawer}
                                        href={link.url}
                                        {...styles.mainNavigationRow}
                                    >
                                        <Typography
                                            className="main-navigation-row-text"
                                            {...styles.mainNavigationRowTypography}
                                        >
                                            {link.title}
                                        </Typography>
                                    </PanelRow>
                                ),
                        )}
                </StyledSection>
                {headerLinkListLinks.length > 0 && (
                    <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                        {headerLinkListLinks.map((link, index) => {
                            /* Valtech Custom Code */
                            const linkFields = headerLinkListLinkFields[index].fields;
                            return (
                                <PanelRow
                                    className="logo-links-row"
                                    key={`${link.url}.${linkFields.overriddenTitle || link.title}`}
                                    {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                    isCurrent={currentPageUrl === link.url}
                                    onClick={closeDrawer}
                                    onKeyPress={(event: React.KeyboardEvent) => {
                                        if (event.key === " " && history && link?.url) {
                                            event.preventDefault();
                                            closeDrawer();
                                            history.push(link.url);
                                        }
                                    }}
                                    href={link.url}
                                    target={linkFields.openInNewWindow ? "_blank" : ""}
                                >
                                    <Typography className="logo-link-title" {...styles.logoLinks?.textWrapper}>
                                        {linkFields.icon ? (
                                            <Icon
                                                src={(headerLinkListIcons as any)[linkFields.icon]?.src}
                                                {...styles.logoLinks?.iconProps}
                                            />
                                        ) : null}
                                        <Typography {...styles.logoLinks?.typographyProps}>
                                            {linkFields.overriddenTitle || link.title /* Valtech Custom Code */}
                                        </Typography>
                                    </Typography>
                                </PanelRow>
                            );
                            /* Valtech Custom Code */
                        })}
                    </StyledSection>
                )}
                {/*
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    <SelectorMenu
                        options={currencies?.map(c => {
                            return {
                                title: c.currencyCode.toUpperCase(),
                                clickableProps: {
                                    onClick: () => {
                                        setCurrency({ currencyId: c.id });
                                        closeDrawer();
                                    },
                                },
                            };
                        })}
                        closeModal={closeDrawer}
                        currentOption={currencies?.find(c => c.id === currentCurrencyId)?.currencyCode}
                        currentOptionIcon={<Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography>}
                    />
                    <SelectorMenu
                        dataTestSelector="mobileLanguageSelector"
                        options={languages?.map(l => {
                            return {
                                title: l.languageCode.toUpperCase(),
                                clickableProps: {
                                    onClick: () => {
                                        setLanguage({ languageId: l.id });
                                        closeDrawer();
                                    },
                                },
                            };
                        })}
                        currentOption={currentLanguage?.languageCode}
                        closeModal={closeDrawer}
                        currentOptionIcon={
                            currentLanguage?.imageFilePath ? (
                                <LogoImage className="language-image" src={currentLanguage.imageFilePath} alt="" />
                            ) : (
                                <Icon className="menu-row-icon" src={Globe} {...styles.menuRowIcon} />
                            )
                        }
                    />
                </StyledSection>
                */}
                {/* Valtech Removed Code - Vmi */}
                {/* Valtech Removed Code - Customer */}
            </Drawer>
        </>
    );
};

const LogoImage = styled.img`
    margin-right: 10px;
    height: 22px;
`;

interface SelectorMenuProps {
    options?: MappedLink[];
    closeModal?: () => void;
    currentOption?: string;
    currentOptionIcon?: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow hasChildren className="logo-links-row" {...styles.logoLinks}>
            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    className="logo-link-title"
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            className="panel-menu"
            currentUrl={currentOption}
            data-test-selector={dataTestSelector}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(NavigationDrawer));
